import React from 'react';
import { motion } from 'framer-motion';

const Section = ({ title, bg = false, children }) => (
  <section className={`py-12 md:py-16 ${bg ? 'bg-gray-50 dark:bg-white/[0.02]' : ''}`}>
    <div className="max-w-4xl mx-auto px-6">
      {title && <h2 className="text-2xl font-display text-gray-900 dark:text-white mb-6 leading-tight">{title}</h2>}
      <div className="prose prose-sm md:prose-base dark:prose-invert max-w-none text-gray-600 dark:text-gray-400 leading-relaxed">
        {children}
      </div>
    </div>
  </section>
);

const PrivacyPolicy = () => {
  const updatedDate = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0f0d]">
      {/* Hero Header */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 px-6 border-b border-gray-100 dark:border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <motion.span
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 mb-6 text-xs font-semibold tracking-[0.2em] uppercase text-brand-600 dark:text-brand-400 border border-brand-200 dark:border-brand-800/50 rounded-full bg-brand-50 dark:bg-brand-900/20"
          >
            Legal
          </motion.span>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-5xl font-display text-gray-900 dark:text-white mb-4"
          >
            Privacy Policy
          </motion.h1>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-gray-500 dark:text-gray-500 text-sm"
          >
            Last Updated: {updatedDate}
          </motion.p>
        </div>
      </section>

      <Section>
        <p>
          Budbreak Innovations Inc. and its affiliates (collectively, "we", "our" or "us") recognize the importance of privacy. The purpose of this privacy statement ("Privacy Statement") is to inform you about our privacy practices, including how we collect, use, and disclose your Personal Information.
        </p>
        <p>
          This Privacy Statement applies to all of our operations in Canada or operations in connection with Canada, unless we have given you a separate privacy statement for a particular product, service, or activity.
        </p>
      </Section>

      <Section title="Meaning of Personal Information" bg>
        <p>
          "Personal Information" means information about an identifiable individual as described under Canadian privacy laws, which may include, but is not limited to, your name, e-mail address, and telephone number. Personal Information does not include any business contact information that is solely used to communicate with you in relation to your employment, business, or profession. Personal Information also does not include information that has been anonymized or aggregated in such a way that there is no serious possibility it can be used to identify an individual, whether on its own or in combination with other information.
        </p>
      </Section>

      <Section title="Personal Information We Collect">
        <h3 className="text-xl font-bold dark:text-white mt-6 mb-4">Products and Services</h3>
        <ul className="list-disc pl-6 mb-8 space-y-2">
          <li>We collect Personal Information that you submit through your use of our products or services, such as your name, e-mail address, business and mailing address, and other contact information.</li>
          <li>We may automatically collect usage data that is identifiable with you when you use our products and services, including location data (GPS) and information relating to the addresses at which our products are deployed.</li>
          <li>We will aggregate and anonymize certain data collected in connection with your use of our products and services, which may be stored and used separately.</li>
        </ul>

        <h3 className="text-xl font-bold dark:text-white mt-6 mb-4">Website</h3>
        <ul className="list-disc pl-6 mb-6 space-y-2">
          <li>Like most websites, we collect technical and device information about your use of our website, which may include your IP address, device type, browser, and navigation history.</li>
          <li>We may use "cookies" to track your preferences and activities on our website.</li>
          <li>If you use our contact forms, we collect your name, email address, the company you represent, and details of your inquiry.</li>
        </ul>
      </Section>

      <Section title="How We Use Your Personal Information" bg>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 list-none pl-0">
          <li className="flex items-start gap-2">
            <span className="text-brand-500 mt-1.5">•</span>
            <span>Supporting your use of our products and services</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-brand-500 mt-1.5">•</span>
            <span>Contacting you regarding technical updates</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-brand-500 mt-1.5">•</span>
            <span>Monitoring and improving performance</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-brand-500 mt-1.5">•</span>
            <span>De-identifying data for research and analysis</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-brand-500 mt-1.5">•</span>
            <span>Developing marketing and educational materials</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-brand-500 mt-1.5">•</span>
            <span>Investigating legal claims or regulatory compliance</span>
          </li>
        </ul>
      </Section>

      <Section title="Information Security">
        <p>
          We have implemented physical, organizational, contractual, and technological security measures with a view to protecting your Personal Information from loss or theft, unauthorized access, disclosure, copying, use, or modification.
        </p>
        <p>
          Despite the measures outlined above, no method of information transmission or information storage is 100% secure. If you have reason to believe that your interaction with us is no longer secure, please contact us immediately.
        </p>
      </Section>

      <Section title="How to Contact Us" bg>
        <div className="p-8 rounded-2xl border border-gray-200 dark:border-white/5 bg-white dark:bg-[#141a17]">
          <p className="mb-4">All comments, questions, concerns or complaints regarding your Personal Information or our privacy practices should be sent to:</p>
          <a href="mailto:privacy@budbreakinnovations.com" className="text-brand-600 dark:text-brand-400 font-semibold hover:underline">
            privacy@budbreakinnovations.com
          </a>
        </div>
      </Section>

      {/* Footer spacer */}
      <div className="py-12"></div>
    </div>
  );
};

export default PrivacyPolicy;